import React, {
  useMemo,
  useState,
} from "react";

import {
  FaCalendarAlt,
  FaFilter,
  FaTable,
  FaTimes,
  FaUsers,
  FaWeight,
} from "react-icons/fa";

import {
  buildStandardReferenceRows,
  buildVeteranReferenceRows,
  rowContainsBirthYear,
} from "../utils/categoryUtils";

import CateButton from "../ui/CateButton";

import {
  fieldClassName,
} from "../ui/FormField";

export default function CategoriesReference({
  season,
}) {
  const [section, setSection] =
    useState("standard");

  const [gender, setGender] =
    useState("ALL");

  const [birthYear, setBirthYear] =
    useState("");

  const rows = useMemo(() => {
    const source =
      section === "veteran"
        ? buildVeteranReferenceRows(
            season
          )
        : buildStandardReferenceRows(
            season
          );

    return source.filter((row) => {
      const matchesGender =
        gender === "ALL" ||
        row.gender === gender;

      const matchesBirthYear =
        rowContainsBirthYear(
          row,
          birthYear,
          season
        );

      return (
        matchesGender &&
        matchesBirthYear
      );
    });
  }, [
    section,
    gender,
    birthYear,
    season,
  ]);

  const yearOptions = useMemo(() => {
    const currentYear =
      new Date().getFullYear();

    return Array.from(
      {
        length: 110,
      },
      (_, index) =>
        String(currentYear - index)
    );
  }, []);

  const resetFilters = () => {
    setGender("ALL");
    setBirthYear("");
  };

  return (
    <section
      id="tableau-categories"
      className="scroll-mt-24"
    >
      <div className="mb-7 text-center">
        <div className="mx-auto inline-flex items-center gap-2 rounded-full bg-orange-100 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-orange-700 dark:bg-orange-500/10 dark:text-orange-300">
          <FaTable />
          Tableau de référence
        </div>

        <h2 className="mt-4 text-3xl font-black text-slate-950 dark:text-white sm:text-4xl">
          Catégories d’âge et de poids
        </h2>

        <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-slate-500 dark:text-slate-400">
          Saison {season}. Filtrez les
          catégories par genre ou année de
          naissance.
        </p>
      </div>

      <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-900">
        <header className="border-b border-slate-200 p-4 dark:border-white/10 sm:p-6">
          <div className="grid gap-4 lg:grid-cols-[auto_1fr] lg:items-end">
            <div>
              <p className="mb-2 text-sm font-black text-slate-700 dark:text-slate-200">
                Type de catégories
              </p>

              <div className="grid grid-cols-2 rounded-2xl bg-slate-100 p-1 dark:bg-white/5">
                <SectionButton
                  active={
                    section === "standard"
                  }
                  icon={FaUsers}
                  onClick={() =>
                    setSection("standard")
                  }
                >
                  Jeunes et adultes
                </SectionButton>

                <SectionButton
                  active={
                    section === "veteran"
                  }
                  icon={FaCalendarAlt}
                  onClick={() =>
                    setSection("veteran")
                  }
                >
                  Vétérans
                </SectionButton>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
              <select
                value={gender}
                onChange={(event) =>
                  setGender(
                    event.target.value
                  )
                }
                className={fieldClassName}
              >
                <option value="ALL">
                  Tous les genres
                </option>
                <option value="M">
                  Masculin
                </option>
                <option value="F">
                  Féminin
                </option>
              </select>

              <select
                value={birthYear}
                onChange={(event) =>
                  setBirthYear(
                    event.target.value
                  )
                }
                className={fieldClassName}
              >
                <option value="">
                  Toutes les années
                </option>

                {yearOptions.map((year) => (
                  <option
                    key={year}
                    value={year}
                  >
                    Né(e) en {year}
                  </option>
                ))}
              </select>

              <CateButton
                icon={FaTimes}
                variant="secondary"
                onClick={resetFilters}
              >
                Effacer
              </CateButton>
            </div>
          </div>
        </header>

        {rows.length ? (
          <>
            <DesktopTable rows={rows} />
            <MobileCards rows={rows} />
          </>
        ) : (
          <div className="p-12 text-center">
            <FaFilter className="mx-auto text-3xl text-slate-300" />

            <h3 className="mt-4 font-black text-slate-950 dark:text-white">
              Aucune catégorie trouvée
            </h3>

            <p className="mt-2 text-sm text-slate-500">
              Modifiez l’année de naissance
              ou le genre sélectionné.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

function SectionButton({
  active,
  icon: Icon,
  children,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex min-h-12 items-center justify-center gap-2 rounded-xl px-4 text-sm font-black transition",
        active
          ? "bg-white text-slate-950 shadow-md dark:bg-slate-700 dark:text-white"
          : "text-slate-500 hover:text-slate-950 dark:text-slate-400 dark:hover:text-white",
      ].join(" ")}
    >
      <Icon />
      {children}
    </button>
  );
}

function DesktopTable({ rows }) {
  return (
    <div className="hidden overflow-x-auto md:block">
      <table className="min-w-full">
        <thead className="bg-slate-950 text-white">
          <tr>
            <TableHeading>
              Catégorie
            </TableHeading>

            <TableHeading>
              Genre
            </TableHeading>

            <TableHeading>
              Âges
            </TableHeading>

            <TableHeading>
              Années de naissance
            </TableHeading>

            <TableHeading>
              Catégories de poids
            </TableHeading>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-slate-200 transition last:border-b-0 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/[0.03]"
            >
              <TableCell>
                <span className="font-black text-slate-950 dark:text-white">
                  {row.categoryLabel}
                </span>
              </TableCell>

              <TableCell>
                <GenderBadge
                  gender={row.gender}
                />
              </TableCell>

              <TableCell>
                {formatAgeRange(row)}
              </TableCell>

              <TableCell>
                <span className="font-bold text-slate-800 dark:text-slate-100">
                  {row.birthYears}
                </span>
              </TableCell>

              <TableCell>
                <WeightPills
                  values={
                    row.weightCategories
                  }
                />
              </TableCell>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MobileCards({ rows }) {
  return (
    <div className="grid gap-4 p-4 md:hidden">
      {rows.map((row) => (
        <article
          key={row.id}
          className="rounded-[1.5rem] border border-slate-200 p-5 dark:border-white/10"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-lg font-black text-slate-950 dark:text-white">
                {row.categoryLabel}
              </p>

              <p className="mt-1 text-sm text-slate-500">
                {formatAgeRange(row)}
              </p>
            </div>

            <GenderBadge
              gender={row.gender}
            />
          </div>

          <div className="mt-4 rounded-xl bg-slate-100 p-3 dark:bg-white/5">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Années de naissance
            </p>

            <p className="mt-1 font-black text-slate-800 dark:text-slate-100">
              {row.birthYears}
            </p>
          </div>

          <div className="mt-4">
            <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
              <FaWeight />
              Catégories de poids
            </p>

            <WeightPills
              values={
                row.weightCategories
              }
            />
          </div>
        </article>
      ))}
    </div>
  );
}

function TableHeading({ children }) {
  return (
    <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-[0.12em] text-slate-300">
      {children}
    </th>
  );
}

function TableCell({ children }) {
  return (
    <td className="px-5 py-4 text-sm text-slate-600 dark:text-slate-300">
      {children}
    </td>
  );
}

function GenderBadge({ gender }) {
  const isFemale = gender === "F";

  return (
    <span
      className={[
        "inline-flex rounded-full px-3 py-1.5 text-xs font-black",
        isFemale
          ? "bg-pink-100 text-pink-700 dark:bg-pink-500/10 dark:text-pink-300"
          : "bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300",
      ].join(" ")}
    >
      {isFemale
        ? "Féminin"
        : "Masculin"}
    </span>
  );
}

function WeightPills({ values }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map((value) => (
        <span
          key={value}
          className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600 dark:bg-white/5 dark:text-slate-300"
        >
          {value}
        </span>
      ))}
    </div>
  );
}

function formatAgeRange(row) {
  if (row.maxAge === Infinity) {
    return `${row.minAge} ans et plus`;
  }

  return `${row.minAge} à ${row.maxAge} ans`;
}